import React from 'react';
import { cn } from '../../ui';

/**
 * A phone bezel around a real App Store screenshot.
 *
 * The shots in `public/img/app/` are the ones on the live App Store listing
 * (id583564306), downloaded at their native 1320×2868 and resized to 660×1434
 * — half native, so they stay sharp on a 2× display at the ~330px they render
 * at, for ~40 KB each.
 *
 * The screenshots already include the iOS status bar and home indicator, so the
 * frame here is only the outer bezel: no notch, no chrome of our own. Anything
 * we drew on top would be a second, contradictory set of system UI.
 *
 * `width`/`height` are set on the <img> so the frame reserves its space before
 * the image decodes — without them a six-up gallery reflows the page as each
 * one lands.
 */

export interface DeviceFrameProps {
  /** Path under `public/`, e.g. `/img/app/home.webp`. */
  src: string;
  /** Describes what the screen shows; empty string marks it decorative. */
  alt: string;
  className?: string;
  /** Above-the-fold frames should load eagerly; gallery frames should not. */
  priority?: boolean;
}

export const DeviceFrame: React.FC<DeviceFrameProps> = ({ src, alt, className, priority }) => (
  <div
    className={cn(
      'overflow-hidden rounded-[2.2rem] border border-white/15 bg-[#0b0d16] p-[7px] shadow-4',
      className
    )}
  >
    <img
      src={src}
      alt={alt}
      width={660}
      height={1434}
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
      fetchPriority={priority ? 'high' : undefined}
      className="block w-full rounded-[1.75rem]"
    />
  </div>
);

export default DeviceFrame;
